/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { Link } from "react-router-dom";
import { Table, Spacer, Tag, Tooltip, Text } from "@nordcloud/gnui";
import { TagWrap } from "~/components/Applications/ApplicationItem";
import { Badge } from "~/components/Badge/Badge";
import { CollapseItem } from "~/components/CollapseItem/CollapseItem";
import { Popover } from "~/components/Popover/Popover";
import { PopoverButton } from "~/components/Popover/PopoverButton";
import { ServiceDeactivate } from "~/components/Services/ServiceDeactivate";
import { DetailsTitle } from "~/components/Styled/Common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { dateFormatWithHours } from "~/constants";
import { ARAttachedServices, ARAttachedSlas } from "~/models";
import { ROUTES } from "~/routing";
import {
  displayServiceHours,
  displaySlaUnitType,
  displaySlaType,
  displayToolColor,
  formatServiceName,
} from "~/tools";
import { AREditSLA } from "./AREditSLA";
import { ARManageServiceTools } from "./ARManageServiceTools";
import { ARUnassignService } from "./ARUnassignService";

type ARServiceItemProps = {
  serviceAttachment: ARAttachedServices;
  refetch: () => void;
  canEdit?: boolean;
};

function ARServiceItem({
  serviceAttachment: { service, attachedSlas, attachedTools },
  refetch,
  canEdit = true,
}: ARServiceItemProps) {
  const [isPopover, setIsPopover] = React.useState(false);
  const [isEmpty, setIsEmpty] = React.useState(true);

  const isDeactivated = service?.deactivated;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    !attachedTools && attachedSlas?.length === 0
      ? setIsEmpty(true)
      : setIsEmpty(false);
  });

  React.useEffect(() => {
    service?.deactivated ? setIsEmpty(true) : setIsEmpty(false);
  }, [service?.deactivated]);

  return (
    <CollapseItem
      buttons={
        <>
          <Tooltip
            status="notification"
            caption="Service Group"
            hideTimeout={0}
          >
            <Tag
              color="notification"
              css={{ margin: "0 1rem 0 auto" }}
              text={service?.serviceGroup?.name}
            />
          </Tooltip>
          {canEdit && (
            <Popover
              alignRight
              setIsPopover={setIsPopover}
              isPopover={isPopover}
              trigger={<PopoverButton />}
            >
              <ARManageServiceTools
                serviceId={service.id}
                attachedTools={attachedTools}
                refetch={refetch}
                setIsPopover={setIsPopover}
              />
              {!isDeactivated && (
                <ARUnassignService service={service} refetch={refetch} />
              )}
              <ServiceDeactivate
                isDeactivated={isDeactivated}
                service={service}
                refetch={refetch}
                setIsPopover={setIsPopover}
                isAssignmentRule
              />
            </Popover>
          )}
        </>
      }
      name={
        <FlexContainer>
          <Link to={`${ROUTES.serviceCommitments.index}/${service.id}`}>
            {formatServiceName(service)}
          </Link>
          <Text tag="div" color="warning" ml="0.5rem">
            {isDeactivated && "(Deactivated)"}
          </Text>
        </FlexContainer>
      }
    >
      {!isEmpty && (
        <div>
          <FlexContainer css={{ margin: "1rem 0 0 0.7rem" }}>
            {attachedTools && (
              <DetailsTitle css={{ minWidth: "3rem", marginBottom: "auto" }}>
                Tools:
              </DetailsTitle>
            )}
            <FlexContainer wrap="wrap" justify="flex-start">
              {attachedTools?.map(({ tool, log }) => {
                const toolType = (type: boolean) =>
                  type === true ? "INTERNAL" : "EXTERNAL";
                const toolIsDeprecated = (isDeprecate: boolean) =>
                  isDeprecate === true ? "DEPRECATED" : "";

                return (
                  <Link
                    key={tool.id}
                    css={{ marginBottom: "1rem" }}
                    to={`${ROUTES.tool.index}/${tool.id}`}
                  >
                    <Tooltip
                      status="notification"
                      caption={`Assigned by: ${log?.created.author}`}
                      hideTimeout={0}
                    >
                      <Badge
                        css={{ margin: "0 0 0 1rem" }}
                        color={displayToolColor(
                          tool.deprecated
                            ? toolIsDeprecated(tool.deprecated)
                            : toolType(tool.internal)
                        )}
                        text={tool.name}
                      />
                    </Tooltip>
                  </Link>
                );
              })}
            </FlexContainer>
          </FlexContainer>
          {attachedSlas?.length !== 0 && (
            <Row>
              <Col>
                <Spacer height="1rem" />
                <Table small>
                  <Table.thead>
                    <Table.tr>
                      <Table.th>SLA Name</Table.th>
                      <Table.th>Description</Table.th>
                      <Table.th>Type</Table.th>
                      <Table.th>Unit Type</Table.th>
                      <Table.th>Value</Table.th>
                      <Table.th>Unit</Table.th>
                      <Table.th>Support service hours</Table.th>
                      <Table.th>Comment</Table.th>
                      <Table.th>Modified</Table.th>
                      {canEdit && (
                        <Table.th css={{ textAlign: "center" }}>
                          Action
                        </Table.th>
                      )}
                    </Table.tr>
                  </Table.thead>
                  <Table.tbody>
                    {attachedSlas?.map((item: ARAttachedSlas) => {
                      return (
                        <Table.tr key={item?.sla?.id}>
                          <Table.td>{item?.sla?.name}</Table.td>
                          <Table.td>{item?.sla?.description}</Table.td>
                          <Table.td>
                            {displaySlaType(item?.sla?.slaType)}
                          </Table.td>
                          <Table.td>
                            {displaySlaUnitType(item?.sla?.unitType)}
                          </Table.td>
                          <Table.td>{item?.effectiveValue}</Table.td>
                          <Table.td>
                            <TagWrap>
                              <Tag text={item?.sla?.range?.unit} />
                            </TagWrap>
                          </Table.td>
                          <Table.td>
                            {displayServiceHours(item?.effectiveSupportHours)}
                          </Table.td>
                          <Table.td>{item?.data?.comment}</Table.td>
                          <Table.td>
                            {item?.data?.log?.modified?.author} <br />
                            {item?.data?.log?.modified?.time &&
                              dayjs(item?.data?.log?.modified?.time).format(
                                `${dateFormatWithHours}`
                              )}
                          </Table.td>
                          {canEdit && (
                            <Table.td hasLeftBorder>
                              <FlexContainer>
                                <AREditSLA
                                  refetch={refetch}
                                  val={item?.effectiveValue}
                                  supportHours={item?.effectiveSupportHours}
                                  comm={item?.data?.comment as string}
                                  sla={item?.sla}
                                  serviceId={service?.id}
                                />
                              </FlexContainer>
                            </Table.td>
                          )}
                        </Table.tr>
                      );
                    })}
                  </Table.tbody>
                </Table>
              </Col>
            </Row>
          )}
        </div>
      )}
    </CollapseItem>
  );
}

export { ARServiceItem };
